import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Dialog, IconButton, Tooltip, Grid, Typography, Divider, makeStyles, Theme } from "@material-ui/core";
import { CloudDownload, PictureAsPdf } from "@material-ui/icons";
import { AxiosError } from "axios";
import { playerCustomHandicap } from "../../types/handicpas";
import HandicapActions from "../../actions/handicaps";
import CSVIcon from "../../assets/images/icons/csv-icon.png";
import { share } from "../../utils/share";
import { Game } from "../../types/game";
import {
  downloadCustomHandicapsCSV,
  getCustomHandicapsCSVFile,
  downloadLeaderboardCSV,
  getLeaderboardCSVFile,
} from "utils/GameReportGenerator/csv";
import { ShareFileButton } from "../SharedComponents/ShareButton";
import {
  exportLeaderboardToImageFile,
  exportLeaderboardToPDF,
  exportResultToImageFile,
  exportResultToPDF,
} from "../../utils/GameReportGenerator/pdf";

interface Props {
  game: Game;
  gameQueryType: string;
  chipClassName?: string;
  iconButtonClassName?: string;
  getHandicapsByRuleId(id: string, callback: (err: AxiosError | null, data: playerCustomHandicap[]) => void): void;
}

const ShareGameInformationDialog = (props: Props) => {
  const { game, gameQueryType, chipClassName, iconButtonClassName } = props;
  const classes = useStyles();
  const iconChipClass = iconButtonClassName || chipClassName || classes.iconAction;
  const [handicaps, setHandicaps] = useState<playerCustomHandicap[]>();
  const [openCH, setOpenCH] = useState<boolean>(false);
  const [openResult, setOpenResult] = useState<boolean>(false);
  const [openLeaderBoard, setOpenLeaderBoard] = useState<boolean>(false);

  useEffect(() => {
    props.getHandicapsByRuleId(game.competition._id, (err, handicaps) => {
      if (!err) setHandicaps(handicaps);
    });
  }, [game]);

  return (
    <>
      {handicaps && (
          <Tooltip title="CSV report" leaveTouchDelay={2000}>
            <IconButton size="small" className={iconChipClass} aria-label="CSV report" onClick={() => setOpenCH(true)}>
              <img src={CSVIcon} alt="" className={classes.csvIcon} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Export result" leaveTouchDelay={2000}>
          <IconButton
            size="small"
            className={iconChipClass}
            aria-label="Export result"
            onClick={() => setOpenResult(true)}
          >
            <CloudDownload fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export leaderboard PDF" leaveTouchDelay={2000}>
          <IconButton
            size="small"
            className={iconChipClass}
            aria-label="Export leaderboard PDF"
            onClick={() => setOpenLeaderBoard(true)}
          >
            <PictureAsPdf fontSize="small" />
          </IconButton>
        </Tooltip>
      {handicaps && (
        <Dialog maxWidth="xs" open={openCH} onBackdropClick={() => setOpenCH(false)}>
          <Grid container>
            <Grid item>
              <div className="flex-column" style={{ padding: "1rem 2rem" }}>
                <Typography>Custom Handicaps CSV</Typography>
                <Divider style={{ marginBottom: 15 }} />

                <Button
                  onClick={() =>
                    share({
                      title: `${game.name} custom handicaps CSV file by SCHALA`,
                      text: `Here is custom handicaps of players in the game ${game.name} which has been played using SCHALA`,
                      url: `https://schala.golf`,
                      files: [getCustomHandicapsCSVFile(`Handicaps.csv`, game, handicaps)],
                    })
                  }
                >
                  Share
                </Button>
                <Button onClick={() => downloadCustomHandicapsCSV(game, handicaps)}>Download</Button>
              </div>
            </Grid>

            <Grid item>
              <div className="flex-column" style={{ padding: "1rem 2rem" }}>
                <Typography>Leaderboard CSV</Typography>
                <Divider style={{ marginBottom: 15 }} />
                <Button
                  onClick={() =>
                    share({
                      title: `${game.name} Leaderboard CSV file by SCHALA`,
                      text: `Here is the leaderboard of ${game.name} game which has been played using SCHALA`,
                      url: `https://schala.golf`,
                      files: [getLeaderboardCSVFile(`leaderboard.csv`, game, gameQueryType)],
                    })
                  }
                >
                  Share
                </Button>
                <Button onClick={() => downloadLeaderboardCSV(game, gameQueryType)}>Download</Button>
              </div>
            </Grid>
          </Grid>
        </Dialog>
      )}
      <Dialog open={openResult} onBackdropClick={() => setOpenResult(false)}>
        <div className="flex-column">
          <ShareFileButton
            title={`SCHALA - ${game.name} game Result`}
            text={`Here is result of ${game.name} which has been played using SCHALA`}
            url={`https://schala.golf`}
            getFiles={async () => await exportResultToImageFile(`GameResult`, game, gameQueryType)}
          />
          <Button onClick={() => exportResultToPDF(game, gameQueryType)}>Download</Button>
        </div>
      </Dialog>

      <Dialog open={openLeaderBoard} onBackdropClick={() => setOpenLeaderBoard(false)}>
        <div className="flex-column">
          <ShareFileButton
            title={`SCHALA - ${game.name} game leaderboard`}
            text={`Here is leaderboard of ${game.name} which has been played using SCHALA`}
            url={`https://schala.golf`}
            getFiles={async () => await exportLeaderboardToImageFile(`Leaderboard`, "leaderboard", game)}
          />
          <Button onClick={() => exportLeaderboardToPDF("leaderboard", game)}>Download</Button>
        </div>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  iconAction: {
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    flexShrink: 0,
    padding: 6,
    borderRadius: 6,
    boxSizing: "border-box",
    color: "#64748b",
    "&:hover": {
      backgroundColor: "rgba(241, 245, 249, 0.95)",
      color: "#334155",
    },
  },
  csvIcon: {
    display: "block",
    width: "auto",
    height: 22,
  },
}));

export default connect(null, { getHandicapsByRuleId: HandicapActions.getHandicapsByRuleId })(
  ShareGameInformationDialog,
);
